import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

const Services = () => {
  const location = useLocation();
  const { t } = useTranslation(); // használjuk a useTranslation hookot a fordításhoz

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const hash = location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  return (
    <div className="services-container">
      <Helmet>
        {/*Meta adatok */}
        <title>Szolgáltatások | Csillag Masszázs Pedikűr</title>
        <meta
          name="description"
          content="Fedezze fel masszázs és gyógypedikűr szolgáltatásainkat Budapesten. Szalonban és kiszállással is elérhető."
        />
        <link
          rel="canonical"
          href="https://janoseszter.com/hu/szolgaltatasok"
        />

        <meta
          property="og:title"
          content="Masszázs és Gyógypedikűr Szolgáltatások Budapesten"
        />
        <meta
          property="og:description"
          content="Svédmasszázs, jóga masszázs, menedzser masszázs és gyógypedikűr Budapesten – igény szerint szalonban vagy háznál."
        />
        <meta
          property="og:url"
          content="https://janoseszter.com/hu/szolgaltatasok"
        />
        <meta
          property="og:image"
          content="https://janoseszter.com/images/logo225.png"
        />
        <meta property="og:site_name" content="janoseszter.com" />
        <meta property="og:type" content="website" />
        {/* Service schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Svédmasszázs",
            provider: {
              "@type": "Person",
              name: "János Eszter",
            },
            areaServed: {
              "@type": "Place",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Budapest",
                addressCountry: "HU",
              },
            },
            description:
              "Svédmasszázs a stresszoldásért és vérkeringés fokozásáért Budapesten, szalonban vagy háznál.",
          })}
        </script>
        {/* FAQ schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Milyen típusú masszázsokat kínál a Csillag Masszázs?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Svédmasszázs, tradicionális thai jóga masszázs és irodai menedzsermasszázs is elérhető, szalonban vagy háznál.",
                },
              },
              {
                "@type": "Question",
                name: "Hogyan történik az időpontfoglalás?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Időpontot online foglalhat a Foglalás gombon keresztül, vagy telefonon is felveheti velünk a kapcsolatot.",
                },
              },
              {
                "@type": "Question",
                name: "Kiknek ajánlott a gyógypedikűr?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "A gyógypedikűr ideális azok számára, akik tyúkszemmel, bőrkeményedéssel, gombásodással vagy diabéteszes lábproblémákkal küzdenek.",
                },
              },
              {
                "@type": "Question",
                name: "Hol érhetők el a szolgáltatások?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "A szolgáltatások Budapesten a Delice Éden Szépségszalonban, valamint előre egyeztetett időpontban háznál is elérhetők.",
                },
              },
              {
                "@type": "Question",
                name: "Mennyi ideig tart egy masszázs?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Általában 60 perc, de igény szerint 30 vagy 90 perces kezelések is elérhetők.",
                },
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "HealthAndBeautyBusiness",
              "name": "Csillag Masszázs Pedikűr",
              "image": "https://janoseszter.com/images/logo225.png",
              "url": "https://janoseszter.com",
              "telephone": "+36 70 554 1606",
              "priceRange": "Ft",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Éden utca 8.",
                "addressLocality": "Budapest",
                "postalCode": "1038",
                "addressCountry": "HU"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 47.5923,
                "longitude": 19.0555
              },
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Saturday", "Sunday"],
                  "opens": "10:00",
                  "closes": "18:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday"
                  ],
                  "opens": "18:00",
                  "closes": "21:00"
                }
              ],
              "sameAs": [
                "https://www.facebook.com/profile.php?id=61551742774521"
              ]
            }
         `}
        </script>
      </Helmet>
      <h1 className="services-title ms-3">{t("Services")}</h1>
      <section id="masszazs" className="service-section">
        <h2>{t("Massage")}:</h2>

        {/* Svéd masszázs */}
        <h3>{t("Massage_sub_1")}</h3>
        <div className="service-item-wrap">
          <img
            src="/articleImages/swedish.jpg"
            alt="Svéd masszázs"
            className="service-image-float"
          />
          <p>{t("Massage_sub_1_text_1")}</p>
          <p>{t("Massage_sub_1_text_2")}</p>
        </div>

        {/* Tradícionális jóga masszázs */}
        <h3>{t("Massage_sub_2")}</h3>
        <div className="service-item-wrap">
          <img
            src="/articleImages/download.jfif"
            alt="Jóga masszázs"
            className="service-image-float"
          />
          <div>
            <p>{t("Massage_sub_2_text_1")}</p>
            <p>{t("Massage_sub_2_text_2")}</p>
          </div>
        </div>

        {/* Menedzser masszázs */}
        <h3>{t("Massage_sub_3")}</h3>
        <div className="service-item-wrap">
          <img
            src="/articleImages/iroda.jpg"
            alt="Manager masszázs"
            className="service-image-float"
          />
          <div>
            <p>{t("Massage_sub_3_text_1")}</p>
            <p>{t("Massage_sub_3_text_2")}</p>
          </div>
        </div>
      </section>
      <section id="gyogypedikur" className="service-section">
        <h2>{t("Pedicure")}</h2>
        <div className="service-item-wrap">
          <img
            src="/articleImages/medical-pedicure-.jpg"
            alt="Manager masszázs"
            className="service-image-float"
          />
          <div>
            <p>{t("Pedicure_sub_1_text_1")}</p>
            <p>{t("Pedicure_sub_1_text_2")}</p>
          </div>
        </div>
      </section>
      <section id="allapotfelmeres" className="service-section">
        <h2>{t("Status")}</h2>
        <p>{t("Status_sub_1_text_1")}</p>
        <p>{t("Status_sub_1_text_2")}</p>
      </section>

      <section id="tanacsadas" className="service-section">
        <h2>{t("Consulting")}</h2>
        <p>{t("Consulting_sub_1_text_1")}</p>
        <p>{t("Consulting_sub_1_text_2")}</p>
      </section>
      <section id="faq" className="faq-section container mt-5">
        <h2>Gyakran Ismételt Kérdések</h2>
        <div className="mb-4">
          <h3>Milyen típusú masszázsokat kínál a Csillag Masszázs?</h3>
          <p>
            Svédmasszázs, tradicionális thai jóga masszázs és irodai
            menedzsermasszázs is elérhető, szalonban vagy háznál.
          </p>
        </div>
        <div className="mb-4">
          <h3>Hogyan történik az időpontfoglalás?</h3>
          <p>
            Időpontot online foglalhat a Foglalás gombon keresztül, vagy
            telefonon is felveheti velünk a kapcsolatot.
          </p>
        </div>
        <div className="mb-4">
          <h3>Kiknek ajánlott a gyógypedikűr?</h3>
          <p>
            A gyógypedikűr ideális azok számára, akik tyúkszemmel,
            bőrkeményedéssel, gombásodással vagy diabéteszes lábproblémákkal
            küzdenek.
          </p>
        </div>
        <div className="mb-4">
          <h3>Hol érhetők el a szolgáltatások?</h3>
          <p>
            A szolgáltatások Budapesten a Delice Éden Szépségszalonban, valamint
            előre egyeztetett időpontban háznál is elérhetők.
          </p>
        </div>
        <div className="mb-4">
          <h3>Mennyi ideig tart egy masszázs?</h3>
          <p>
            Általában 60 perc, de igény szerint 30 vagy 90 perces kezelések is
            elérhetők.
          </p>
        </div>
      </section>

      <div className="back-button-container ms-4">
        <Link to="/" className="back-button">
          ⬅ Vissza a főoldalra
        </Link>
      </div>
    </div>
  );
};

export default Services;
